import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { MEASUREMENT_PROFILES, type MeasurementProfile, type MeasurementSystem } from '../lib/measurementSystem'
import { resolveMeasurementSystem } from '../lib/measurement'

interface MeasurementContextValue {
  system: MeasurementSystem
  profile: MeasurementProfile
  locale: string
}
const MeasurementContext = createContext<MeasurementContextValue | null>(null)

export function useOptionalMeasurements() {
  return useContext(MeasurementContext)
}

/** Controlled, scoped provider. Supply project, historical snapshot or company choice explicitly. */
export function MeasurementProvider({ system, locale = 'fr-FR', children }: {
  system: MeasurementSystem | null | undefined
  locale?: string
  children: ReactNode
}) {
  const resolved = resolveMeasurementSystem(system)
  const value = useMemo(() => ({ system: resolved, profile: MEASUREMENT_PROFILES[resolved], locale }), [resolved, locale])
  return <MeasurementContext.Provider value={value}>{children}</MeasurementContext.Provider>
}

export function useMeasurements(): MeasurementContextValue {
  const context = useContext(MeasurementContext)
  if (!context) throw new Error('useMeasurements requires a scoped MeasurementProvider')
  return context
}
